// Launch > drilldown: the rows behind the number that was just clicked.
//
// Every tile on the launch dashboard is an aggregate; this modal is the answer
// to the question an operator asks the moment a number looks interesting: WHO?
// Which clients signed up, which projects were opened, which searches ran. The
// response's `total` is computed with the same predicate as the tile, so the
// header always repeats the number that was clicked; `rows` is the newest slice
// of that population, and each row links to the entity's own page (project
// first, else user) in the Explorer.
//
// Employee sessions only. The kiosk (TV) renders the same tiles UNCLICKABLE:
// its remote-view token buys the PII-free aggregate read and nothing else, and
// this modal is precisely the payload that token must never see.
import { Badge, Button } from "@recruiter/shared";
import type {
  LaunchDrilldownMetric,
  LaunchDrilldownRow,
  LaunchDrilldownWindow,
} from "@recruiter/shared/types";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { formatDateTime, formatStatValue } from "../lib/format";
import { launchDrilldownQuery } from "../lib/queries";
import { ErrorState, LoadingState } from "./states";

/** What each tile opens: the metric, the window its number shows, and the human
 *  label the modal is titled with. */
export interface LaunchDrill {
  metric: LaunchDrilldownMetric;
  window: LaunchDrilldownWindow;
  label: string;
}

const WINDOW_LABELS: Record<LaunchDrilldownWindow, string> = {
  "1h": "Last hour",
  today: "Today",
  "24h": "24 hours",
  // horizon-allow: a user-chosen window, not a claim about the data
  all: "All time",
};

/** Where a row leads. Project page first (most rows are about a conversation),
 *  else the user's page, else nowhere (an error row whose project is gone). */
function rowLink(row: LaunchDrilldownRow): { to: string; params: Record<string, string> } | null {
  if (row.projectId) {
    return { to: "/explorer/projects/$projectId", params: { projectId: row.projectId } };
  }
  if (row.userId) {
    return { to: "/explorer/users/$id", params: { id: row.userId } };
  }
  return null;
}

function DrillRow({ row }: { row: LaunchDrilldownRow }) {
  const link = rowLink(row);
  return (
    <tr className={link ? "lp-drill__row lp-drill__row--link" : "lp-drill__row"}>
      <td>
        {link ? (
          <Link to={link.to} params={link.params} className="ex-primary-link">
            {row.title}
          </Link>
        ) : (
          <span className="rc-cell-strong">{row.title}</span>
        )}
        {row.sub ? <span className="rc-cell-sub">{row.sub}</span> : null}
      </td>
      <td>
        {row.meta ? (
          <Badge tone="neutral" size="sm">
            {row.meta}
          </Badge>
        ) : null}
      </td>
      <td className="lp-drill__at">{formatDateTime(row.at ?? null)}</td>
      <td>
        {link ? (
          <Link
            to={link.to}
            params={link.params}
            className="ex-row-link"
            aria-label={`Open ${row.title}`}
          >
            {"→"}
          </Link>
        ) : null}
      </td>
    </tr>
  );
}

export function LaunchDrilldownModal({
  drill,
  onClose,
}: {
  drill: LaunchDrill;
  onClose: () => void;
}) {
  // The window is modal-local state seeded from the tile that opened it, so
  // "Signups, last hour" opens on the hour and one click flips to today.
  const [window, setWindow] = useState<LaunchDrilldownWindow>(drill.window);
  const query = useQuery(launchDrilldownQuery(drill.metric, window));

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The switcher renders from the RESPONSE (the windows this metric's tile
  // actually shows); until it arrives, the seed window alone.
  const windows = query.data?.windows ?? [drill.window];
  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;

  return (
    // Backdrop click closes; clicks inside the panel stay inside.
    <div
      className="lp-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`${drill.label}: the rows behind the number`}
      onClick={onClose}
    >
      <div
        className="lp-modal__panel lp-modal__panel--drill"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="lp-drill__head">
          <h2 className="lp-modal__title">{drill.label}</h2>
          {query.data ? <span className="lp-drill__total">{formatStatValue(total)}</span> : null}
          <div className="lp-drill__windows" role="tablist" aria-label="Time window">
            {windows.map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={window === key}
                className={`lp-drill__window${window === key ? " lp-drill__window--active" : ""}`}
                onClick={() => setWindow(key)}
              >
                {WINDOW_LABELS[key]}
              </button>
            ))}
          </div>
        </header>

        {query.isPending ? <LoadingState label="Finding the rows…" /> : null}
        {query.isError ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : null}

        {query.data ? (
          rows.length === 0 ? (
            <p className="ex-muted">Nothing in this window.</p>
          ) : (
            <>
              <div className="rc-table-wrap lp-drill__scroll">
                <table className="rc-table">
                  <tbody>
                    {rows.map((row) => (
                      <DrillRow key={`${row.kind}-${row.id}`} row={row} />
                    ))}
                  </tbody>
                </table>
              </div>
              {/* A capped list says so instead of impersonating completeness.
                  The grouped metrics legitimately show fewer rows than the
                  total (messages roll up per project), so only a CAPPED list
                  gets the caveat. */}
              {total > rows.length && rows.length >= 200 ? (
                <p className="lp-drill__cap">
                  Showing the latest {rows.length} of {formatStatValue(total)}.
                </p>
              ) : null}
            </>
          )
        ) : null}

        <div className="lp-modal__actions">
          <Button variant="outline" tone="neutral" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
